import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, X } from "lucide-react";

/**
 * "No access" for the whole panel.
 *
 * The backend refuses a staff member who lacks a module with
 * 403 { code: "PERMISSION_DENIED", module, needs, has, message }
 * (middleware/staffPermissions.js). Screens fetch with plain axios and many of
 * them swallow errors, so instead of teaching every screen the code, any
 * response carrying it raises one notice here with the server's own sentence
 * ("You can view Leads but not change it. Ask an admin for edit access.").
 */

export const PERMISSION_EVENT = "olli:permission-denied";

export const isPermissionDenied = (error) => error?.response?.status === 403 && error?.response?.data?.code === "PERMISSION_DENIED";

/** Announce a refusal; safe to call from any axios instance's response interceptor. */
export function announcePermissionDenied(error) {
  if (!isPermissionDenied(error)) return;
  const data = error.response.data;
  window.dispatchEvent(new CustomEvent(PERMISSION_EVENT, { detail: { module: data.module, needs: data.needs, message: data.message } }));
}

// The default axios instance, used by the Olli screens.
let installed = false;
export function installPermissionInterceptor(instance = axios) {
  if (instance === axios && installed) return;
  if (instance === axios) installed = true;
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      announcePermissionDenied(error);
      return Promise.reject(error);
    },
  );
}

export function PermissionNotice() {
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    installPermissionInterceptor();
    const onDenied = (e) => setNotice({ ...e.detail, at: Date.now() });
    window.addEventListener(PERMISSION_EVENT, onDenied);
    return () => window.removeEventListener(PERMISSION_EVENT, onDenied);
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  if (!notice) return null;
  const title = notice.needs === "edit" ? "View only" : "No access";
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[90] flex justify-center px-4" role="alert">
      <div className="pointer-events-auto flex max-w-lg animate-nav-in items-start gap-3 rounded-lg bg-ink py-3 pl-4 pr-3 text-paper shadow-lift">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-paper/10">
          <Lock size={14} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-small font-semibold">{title}{notice.module ? ` · ${notice.module}` : ""}</p>
          <p className="mt-0.5 text-caption text-paper/75">{notice.message || "You don't have access to this. Ask an admin to give you access."}</p>
        </div>
        <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="flex h-7 w-7 items-center justify-center rounded-full text-paper/60 hover:bg-paper/10 hover:text-paper">
          <X size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
